// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  HoverDatum,
  getRemainingLifeMonths,
  getTooltipInitialCostBasis,
  getTooltipPeriodSubLabel,
  showCostTransitionDetail,
} from '@/asset/components/depreciation-chart-utilities';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { pluralize } from '@/utilities/pluralize';
import { Box, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

interface Props {
  acquisitionCost: number | null;
  capitalizedCost: number | null;
  endOfUsefulLife: DateTime | null;
  exitDate: DateTime | null;
  inServiceDate: DateTime | null;
  currencyCode: IsoCurrencyCode;
  salvageValue: number | null;
  themeBorderColor: string;
  tooltipData: HoverDatum;
  usefulLifeMonths: number | null;
}

export function DepreciationChartTooltip(props: Props) {
  const formatMoney = (value: number | null) =>
    formatAmountWithCurrency(
      value === null ? null : new CurrencyDecimal(value),
      props.currencyCode
    );
  const remainingLifeMonths = getRemainingLifeMonths({
    date: props.tooltipData.date,
    endOfUsefulLife: props.endOfUsefulLife,
    exitDate: props.exitDate,
    inServiceDate: props.inServiceDate,
  });

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography color="inherit" fontWeight={600} variant="body2">
          {props.tooltipData.date.toFormat('LLLL yyyy')}
        </Typography>
        <Typography color="inherit" sx={{ opacity: 0.8 }} variant="caption">
          {getTooltipPeriodSubLabel({
            date: props.tooltipData.date,
            inServiceDate: props.inServiceDate,
            usefulLifeMonths: props.usefulLifeMonths,
          })}
          {remainingLifeMonths !== null &&
            ` (${pluralize(remainingLifeMonths, { singular: 'month' })} remaining)`}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography color="inherit" variant="body2">
            Depreciation in month
          </Typography>
          <Typography color="inherit" fontWeight={600} variant="body2">
            {formatMoney(props.tooltipData.depreciation)}
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          border: `1px solid ${props.themeBorderColor}`,
          borderRadius: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <Stack spacing={0.75}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography color="inherit" variant="body2">
              Initial cost basis
            </Typography>
            <Typography color="inherit" fontWeight={600} variant="body2">
              {formatMoney(
                getTooltipInitialCostBasis({
                  acquisitionCost: props.acquisitionCost,
                  capitalizedCost: props.capitalizedCost,
                  inServiceDate: props.inServiceDate,
                  tooltipDate: props.tooltipData.date,
                })
              )}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography color="inherit" variant="body2">
              Less accumulated depreciation
            </Typography>
            <Typography color="inherit" fontWeight={600} variant="body2">
              {formatMoney(props.tooltipData.accumulated)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{
              borderTop: `1px solid ${props.themeBorderColor}`,
              pt: 0.75,
            }}
          >
            <Stack spacing={0.25}>
              <Typography color="inherit" fontWeight={700} variant="body2">
                Net book value
              </Typography>
              {props.salvageValue !== null && (
                <Typography
                  color="inherit"
                  sx={{ fontStyle: 'italic', opacity: 0.8 }}
                  variant="caption"
                >
                  Includes {formatMoney(props.salvageValue)} salvage value
                </Typography>
              )}
            </Stack>
            <Typography color="inherit" fontWeight={700} variant="body2">
              {formatMoney(props.tooltipData.bookValue)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      {showCostTransitionDetail({
        acquisitionCost: props.acquisitionCost,
        capitalizedCost: props.capitalizedCost,
        inServiceDate: props.inServiceDate,
        tooltipDate: props.tooltipData.date,
      }) && (
        <Typography color="inherit" variant="body2">
          Cost basis steps from {formatMoney(props.acquisitionCost)} to{' '}
          {formatMoney(props.capitalizedCost)}
        </Typography>
      )}
    </Stack>
  );
}
